/**
 * Vue Modal Plugin
 *
 * Centralized modal management using an event-driven architecture. The modal manager
 * emits 'toggle' events via mitt, which modal components subscribe to for visibility changes.
 *
 * Usage:
 * ```typescript
 * const modal = inject<ModalManager>('$modal');
 * modal.show('modal-name', { userId: 123 });
 * modal.hide('modal-name');
 * modal.closeAllModals();
 * ```
 */
import type { App, Plugin } from 'vue';
import mitt, { Emitter } from 'mitt';
import ActivixModal from '@/components/elements/ActivixModal.vue';

type ModalEvents = {
    toggle: { name: string; visible: boolean; params?: any };
};

interface ModalManager {
    show(name: string, params?: any): void;
    hide(name: string, params?: any): void;
    toggle(name: string, params?: any): void;
    closeAllModals(): void;
    subscription: Emitter<ModalEvents>;
}

function createModalManager(): ModalManager {
    const eventBus = mitt<ModalEvents>();
    const openModals = new Set<string>();

    return {
        subscription: eventBus,
        show(name: string, params?: any) {
            openModals.add(name);
            eventBus.emit('toggle', { name, visible: true, params });
        },
        hide(name: string, params?: any) {
            openModals.delete(name);
            eventBus.emit('toggle', { name, visible: false, params });
        },
        toggle(name: string, params?: any) {
            // Check if modal is currently open and toggle accordingly
            const isOpen = openModals.has(name);
            if (isOpen) {
                openModals.delete(name);
                eventBus.emit('toggle', { name, visible: false, params });
            } else {
                openModals.add(name);
                eventBus.emit('toggle', { name, visible: true, params });
            }
        },
        closeAllModals() {
            // Close all tracked open modals by emitting hide events
            const modalsToClose = Array.from(openModals);

            if (modalsToClose.length > 0) {
                modalsToClose.forEach((name) => {
                    eventBus.emit('toggle', { name, visible: false, params: undefined });
                });
                // Clear the set after emitting all events
                openModals.clear();
            }

            // Also do DOM cleanup as a safeguard for any modals that might not be tracked
            document.querySelectorAll('.modal').forEach((modal) => {
                modal.classList.remove('show');
                (modal as HTMLElement).style.display = 'none';
            });
            document.querySelectorAll('.modal-backdrop').forEach((backdrop) => {
                backdrop.remove();
            });
            document.body.classList.remove('modal-open');
        },
    };
}

const VueModalPlugin: Plugin = {
    install(app: App) {
        const modalManager = createModalManager();

        // Provide the modal manager for inject
        app.provide('$modal', modalManager);

        // Register ActivixModal component globally
        app.component('ActivixModal', ActivixModal);
    },
};

export default VueModalPlugin;
export type { ModalManager, ModalEvents };
